import { ChevronDown } from "@/components/bnp-icons";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export interface TProfessionalsItem {
  title: string;
  path: string;
  activePaths: string[];
}

interface ProfessionalsDropdownProps {
  items: TProfessionalsItem[];
  pathname: string | null;
}

const ProfessionalsDropdown = ({
  items,
  pathname,
}: ProfessionalsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setIsOpen(false), 250);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  const isRouteActive = (activePaths: string[]) =>
    activePaths.some(
      (path) =>
        path !== "/" && (pathname === path || pathname?.startsWith(`${path}/`)),
    );

  const isActive = items.some((item) => isRouteActive(item.activePaths));

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={`relative flex items-center gap-1 py-2 text-[15px] font-semibold transition-colors hover:text-bnp-cyan whitespace-nowrap ${
          isOpen || isActive ? "text-bnp-cyan" : "text-blue-primary"
        }`}
      >
        Professionals
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
        {isActive && (
          <span className="absolute bottom-0 left-0 w-full h-0.5 bg-bnp-cyan" />
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 overflow-hidden rounded-lg border border-soft-200 bg-white shadow-lg">
          {items.map((item) => (
            <Link
              href={item.path}
              key={item.path}
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 ${
                isRouteActive(item.activePaths)
                  ? "text-bnp-cyan bg-soft-200"
                  : "text-blue-primary"
              }`}
            >
              {item.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfessionalsDropdown;
